import { useCallback, useMemo, useRef, useState } from 'react'

type TimelineRange = {
  start: number
  end: number
}

type UseTimelineZoomInteractionOptions = {
  maxTime: number
  visibleRange: TimelineRange
  timelineWidth: number
  minSelectionDuration?: number
  minDragDistancePx?: number
  disabled?: boolean
  onZoom: (range: TimelineRange) => void
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const normalizeRange = (start: number, end: number): TimelineRange => ({
  start: Math.min(start, end),
  end: Math.max(start, end),
})

const useTimelineZoomInteraction = ({
  maxTime,
  visibleRange,
  timelineWidth,
  minSelectionDuration = 1,
  minDragDistancePx = 4,
  disabled = false,
  onZoom,
}: UseTimelineZoomInteractionOptions) => {
  const [selectionRange, setSelectionRange] = useState<TimelineRange | null>(
    null,
  )
  const dragStateRef = useRef<{
    pointerId: number
    startedAt: number
    startedAtX: number
    rootLeft: number
    rootWidth: number
    moved: boolean
  } | null>(null)
  const lastDragFinishedAtRef = useRef(0)

  const safeWidth = Math.max(timelineWidth, 1)
  const safeVisibleStart = visibleRange.start
  const safeVisibleEnd = Math.max(visibleRange.end, visibleRange.start + 1)
  const visibleDuration = safeVisibleEnd - safeVisibleStart

  const toTime = useCallback(
    (localX: number) => {
      const ratio = clamp(localX / safeWidth, 0, 1)
      const time = safeVisibleStart + ratio * visibleDuration
      return Number(clamp(time, 0, maxTime).toFixed(2))
    },
    [maxTime, safeVisibleStart, safeWidth, visibleDuration],
  )

  const toX = useCallback(
    (time: number) => {
      const clampedTime = clamp(time, safeVisibleStart, safeVisibleEnd)
      return ((clampedTime - safeVisibleStart) / visibleDuration) * safeWidth
    },
    [safeVisibleEnd, safeVisibleStart, safeWidth, visibleDuration],
  )

  const onPointerDown = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (disabled) return
      if (event.button !== 0) return
      const rect = event.currentTarget.getBoundingClientRect()
      const startX = clamp(event.clientX - rect.left, 0, rect.width)
      const startTime = toTime(startX)
      const pointerId = event.pointerId
      dragStateRef.current = {
        pointerId,
        startedAt: startTime,
        startedAtX: startX,
        rootLeft: rect.left,
        rootWidth: rect.width,
        moved: false,
      }
      setSelectionRange(null)
      const onWindowPointerMove = (moveEvent: PointerEvent) => {
        const dragState = dragStateRef.current
        if (!dragState || dragState.pointerId !== moveEvent.pointerId) return
        const localX = clamp(
          moveEvent.clientX - dragState.rootLeft,
          0,
          dragState.rootWidth,
        )
        const nextTime = toTime(localX)
        if (Math.abs(localX - dragState.startedAtX) >= minDragDistancePx) {
          dragState.moved = true
        }
        setSelectionRange(
          dragState.moved
            ? normalizeRange(dragState.startedAt, nextTime)
            : null,
        )
      }

      const onWindowPointerUp = (upEvent: PointerEvent) => {
        const dragState = dragStateRef.current
        if (!dragState || dragState.pointerId !== upEvent.pointerId) return
        const localX = clamp(
          upEvent.clientX - dragState.rootLeft,
          0,
          dragState.rootWidth,
        )
        const endTime = toTime(localX)
        const nextRange = normalizeRange(dragState.startedAt, endTime)
        if (
          dragState.moved &&
          nextRange.end - nextRange.start >= minSelectionDuration
        ) {
          onZoom(nextRange)
          lastDragFinishedAtRef.current = Date.now()
        }
        dragStateRef.current = null
        setSelectionRange(null)
        window.removeEventListener('pointermove', onWindowPointerMove)
        window.removeEventListener('pointerup', onWindowPointerUp)
      }

      window.addEventListener('pointermove', onWindowPointerMove)
      window.addEventListener('pointerup', onWindowPointerUp)
    },
    [disabled, minDragDistancePx, minSelectionDuration, onZoom, toTime],
  )

  const onPointerMove = useCallback(() => undefined, [])
  const onPointerUp = useCallback(() => undefined, [])
  const onPointerCancel = useCallback(() => {
    dragStateRef.current = null
    setSelectionRange(null)
  }, [])

  const selectionPixels = useMemo(() => {
    if (!selectionRange) return null
    const startX = toX(selectionRange.start)
    const endX = toX(selectionRange.end)
    return {
      startX: Math.min(startX, endX),
      endX: Math.max(startX, endX),
    }
  }, [selectionRange, toX])

  return {
    toTime,
    toX,
    selectionRange,
    selectionPixels,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    lastDragFinishedAtRef,
  }
}

export default useTimelineZoomInteraction
export type { TimelineRange }
